import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

import { BoardDropdownMenu } from "@/components/QuickSearch/components/BoardDropdown/components/BoardDropdownMenu";
import { BoardValue } from "@/components/QuickSearch/components/BoardDropdown/components/BoardValue";
import type { BoardDropdownOption } from "@/components/QuickSearch/components/BoardDropdown/models/boardDropdownOption";
import { DropdownList } from "@/components/QuickSearch/components/DropdownList/DropdownList";
import type { DropdownOptionBase } from "@/components/QuickSearch/components/DropdownList/models/dropdownOptions";
import type { MenuRendererProps } from "@/components/QuickSearch/components/DropdownList/models/menuRenderProps";
import type { SearchFn } from "@/components/QuickSearch/hooks/useSearchableOptions";
import useSearchableOptions from "@/components/QuickSearch/hooks/useSearchableOptions";

type BoardDropdownProps = {
  value: BoardDropdownOption | null;
  options: BoardDropdownOption[];
  onOptionSelect: (data: DropdownOptionBase) => void;
  isLoadingBoards: boolean;
  isOptionSelected: boolean;
};
export const BoardDropdown: React.FC<BoardDropdownProps> = ({ value, options, onOptionSelect, isOptionSelected }) => {
  const onSearch: SearchFn<BoardDropdownOption> = useCallback(
    (term, searchableOptions) => searchableOptions.filter((x) => x.label.toLocaleLowerCase().includes(term)),
    []
  );

  const { searchedOptions, onInputChange } = useSearchableOptions(options, onSearch);

  const valueRender = useCallback((props: DropdownOptionBase): JSX.Element => {
    const value: BoardDropdownOption = props as BoardDropdownOption;
    const { workspace } = value;
    const boardValueStyles = {
      padding: "0 0 0 8px",
    };

    return <BoardValue value={props.value} workspace={workspace} label={props.label} style={boardValueStyles} />;
  }, []);

  const menuRender = React.useCallback((props: MenuRendererProps<object>): JSX.Element => {
    const value: MenuRendererProps<BoardDropdownOption> = props as MenuRendererProps<BoardDropdownOption>;
    return <BoardDropdownMenu {...value} />;
  }, []);

  const isFocusedOnInit = useRef(false);

  useEffect(() => {
    if (isOptionSelected || isFocusedOnInit.current) {
      isFocusedOnInit.current = true;
      return;
    }

    setTimeout(() => {
      const boardDropdown: HTMLInputElement | null = document.querySelector(".board-dropdown input");
      boardDropdown?.focus();
    }, 10);
  }, [isOptionSelected]);

  const onOptionSelectInternal = useCallback(
    (data: DropdownOptionBase) => {
      onOptionSelect?.(data);

      setTimeout(() => {
        const itemDropDown: HTMLInputElement | null = document.querySelector(".item-dropdown input");
        itemDropDown?.focus();
      }, 10);
    },
    [onOptionSelect]
  );

  return (
    <DropdownListWrapper
      value={value}
      options={searchedOptions}
      menuRenderer={menuRender}
      onOptionSelect={onOptionSelectInternal}
      placeholder="Select a board"
      clearable={false}
      valueRenderer={valueRender}
      onInputChange={onInputChange}
      tabIndex={100}
      className="board-dropdown"
      onKeyDown={() => {}}
      loadingMessage="Loading boards"
      isBoardSelected={!!value}
    />
  );
};

const DropdownListWrapper = styled(DropdownList)<{ isBoardSelected: boolean }>`
  width: 300px;
`;
